import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Target, Sparkles, RefreshCw, AlertTriangle, Clock,
  CheckCircle2, XCircle, Mail, ChevronRight, ChevronDown,
  Send, Eye, Loader2, Users, Package, Zap,
  MessageSquareText,
} from 'lucide-react';
import {
  Card, CardContent,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ── Decision-state visual config ──────────────────────────────────────
const STATE_META = {
  needs_decision: {
    label: 'Needs Decision',
    short: 'Decide',
    icon:  AlertTriangle,
    accent:'border-l-red-500',
    pill:  'bg-red-50 text-red-700 border-red-200',
    dot:   'bg-red-500',
    description: 'Auditor must act — escalation, missed commitment, or unsent findings',
  },
  awaiting: {
    label: 'Awaiting Reply',
    short: 'Awaiting',
    icon:  Clock,
    accent:'border-l-amber-500',
    pill:  'bg-amber-50 text-amber-700 border-amber-200',
    dot:   'bg-amber-500',
    description: 'Email sent — waiting for the team to respond',
  },
  in_progress: {
    label: 'In Progress',
    short: 'In Progress',
    icon:  Zap,
    accent:'border-l-blue-500',
    pill:  'bg-blue-50 text-blue-700 border-blue-200',
    dot:   'bg-blue-500',
    description: 'Replies received — commitments being tracked',
  },
  resolved: {
    label: 'Resolved',
    short: 'Resolved',
    icon:  CheckCircle2,
    accent:'border-l-emerald-500',
    pill:  'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot:   'bg-emerald-500',
    description: 'AI confirms resolved or audit is all-clear',
  },
};

const COMMIT_STATUS_META = {
  honoured: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  late:     { cls: 'bg-amber-50 text-amber-700 border-amber-200',       icon: AlertTriangle },
  missed:   { cls: 'bg-red-50 text-red-700 border-red-200',             icon: XCircle },
  pending:  { cls: 'bg-slate-50 text-slate-700 border-slate-200',       icon: Clock },
};

// ── Utils ──────────────────────────────────────────────────────────────
const relTime = (iso) => {
  if (!iso) return '';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 60_000) return 'just now';
    if (diff < 3_600_000) return `${Math.floor(diff/60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff/3_600_000)}h ago`;
    if (diff < 604_800_000) return `${Math.floor(diff/86_400_000)}d ago`;
    return `${Math.floor(diff/604_800_000)}w ago`;
  } catch { return iso; }
};

// ── Stat tile ─────────────────────────────────────────────────────────
function StateTile({ stateKey, count, active, onClick }) {
  const meta = STATE_META[stateKey];
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`state-tile-${stateKey}`}
      className={`text-left px-4 py-3 rounded-xl border transition-all ${
        active
          ? `${meta.pill} border-current shadow-sm`
          : 'bg-white border-border hover:border-primary/30 hover:shadow-sm text-muted-foreground'
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <Icon className={`w-4 h-4 ${active ? '' : 'opacity-60'}`} />
        <span className={`text-2xl font-bold tabular-nums ${active ? '' : 'text-foreground'}`}>{count ?? 0}</span>
      </div>
      <p className={`text-[11px] font-bold uppercase tracking-wider ${active ? '' : 'text-muted-foreground'}`}>
        {meta.label}
      </p>
    </button>
  );
}

// ── Audit Card ────────────────────────────────────────────────────────
function AuditCard({ a, expanded, onToggle, onOpen, onRefresh, refreshing }) {
  const meta = STATE_META[a.decision_state] || STATE_META.in_progress;
  const StateIcon = meta.icon;
  const rec = a.rec_summary || {};
  const ai  = a.ai || {};

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      className={`rounded-xl border-l-4 bg-white border border-border/60 hover:shadow-md transition-shadow ${meta.accent} ${a.has_new_activity ? 'ring-1 ring-blue-300/60 shadow-[0_0_0_3px_rgba(59,130,246,0.06)]' : ''}`}
      data-testid={`audit-card-${a._id}`}
    >
      <div className="p-4">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            {/* Subject + week + NEW badge */}
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              {typeof a.week_number === 'number' && (
                <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-bold tracking-wider">
                  W{a.week_number}
                </span>
              )}
              <h3 className="text-sm font-semibold text-foreground truncate" title={a.thread_subject || ''}>
                {a.thread_subject || `Audit · ${a.country || '—'}`}
              </h3>
              {a.has_new_activity && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-bold uppercase tracking-wider border border-blue-200 animate-pulse"
                  data-testid={`audit-new-badge-${a._id}`}
                  title={a.new_replies_count
                    ? `${a.new_replies_count} new ${a.new_replies_count === 1 ? 'message' : 'messages'} since you last viewed this audit`
                    : 'New audit you haven\'t opened yet'}
                >
                  <Sparkles className="w-2.5 h-2.5" />
                  {a.new_replies_count > 0
                    ? `${a.new_replies_count} new ${a.new_replies_count === 1 ? 'reply' : 'replies'}`
                    : 'NEW'}
                </span>
              )}
            </div>

            {/* Meta row: state pill + counts */}
            <div className="flex items-center gap-2 flex-wrap text-[11px]">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${meta.pill}`}>
                <StateIcon className="w-2.5 h-2.5" />{meta.label}
              </span>

              {a.series_display && (
                <span className="text-muted-foreground">{a.series_display}</span>
              )}
              {a.team_member?.name && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Users className="w-3 h-3" />{a.team_member.name}
                </span>
              )}
              <span className="text-muted-foreground">·  {relTime(a.created_at)}</span>
            </div>

            {/* AI headline */}
            {ai.headline && (
              <p className="mt-2 text-xs text-foreground/70 italic line-clamp-2 max-w-3xl">
                “{ai.headline}”
              </p>
            )}

            {/* State reason (only when nothing else explains it) */}
            {!ai.headline && a.state_reason && (
              <p className="mt-2 text-xs text-muted-foreground">
                {a.state_reason}
              </p>
            )}

            {/* Quick stats strip */}
            <div className="mt-3 flex items-center gap-3 flex-wrap text-[11px]">
              <span className="flex items-center gap-1 text-muted-foreground">
                <Package className="w-3 h-3" />
                {(rec.total ?? rec.allow + rec.review + rec.reject) || 0} SKUs
              </span>
              {(rec.reject || 0) > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-semibold border border-red-200">
                  {rec.reject} HIGH ALERT
                </span>
              )}
              {(rec.review || 0) > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-semibold border border-amber-200">
                  {rec.review} ALERT
                </span>
              )}
              {(rec.allow || 0) > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium border border-emerald-200">
                  {rec.allow} ALLOW
                </span>
              )}
              <span className="text-muted-foreground/60">·</span>
              <span className="flex items-center gap-1 text-muted-foreground">
                <Mail className="w-3 h-3" />
                {a.thread_messages || 0} msg{a.thread_messages !== 1 ? 's' : ''}
              </span>
              {ai.commitments_total > 0 && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <MessageSquareText className="w-3 h-3" />
                  {ai.commitments_total} commit{ai.commitments_total !== 1 ? 's' : ''}
                  {ai.commitments_honoured > 0 && (
                    <span className="ml-1 text-emerald-700 font-medium">{ai.commitments_honoured}✓</span>
                  )}
                  {ai.commitments_missed > 0 && (
                    <span className="ml-1 text-red-700 font-medium">{ai.commitments_missed}✗</span>
                  )}
                </span>
              )}
              {a.email_sent_at && (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Send className="w-3 h-3" />sent {relTime(a.email_sent_at)}
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-1.5 flex-shrink-0">
            <Button
              size="sm"
              onClick={() => onOpen(a)}
              className="h-7 text-[11px] gap-1 px-2.5"
              data-testid={`audit-open-${a._id}`}
            >
              <Eye className="w-3 h-3" />Open
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={() => onRefresh(a)}
              disabled={refreshing}
              className="h-7 text-[11px] gap-1 px-2.5 text-muted-foreground"
              data-testid={`audit-refresh-${a._id}`}
            >
              {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Refresh
            </Button>
          </div>
        </div>

        {/* Expandable detail */}
        {(ai.commitments_total > 0 || ai.next_step) && (
          <button
            type="button"
            onClick={onToggle}
            className="mt-3 flex items-center gap-1 text-[11px] text-primary hover:underline"
            data-testid={`audit-expand-${a._id}`}
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {expanded ? 'Hide details' : `Show details (${ai.commitments_total} commitment${ai.commitments_total !== 1 ? 's' : ''})`}
          </button>
        )}
      </div>

      {/* Expanded body */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 border-t border-border/50 bg-secondary/20 space-y-3">
              {/* Commitments */}
              {ai.commitments?.length > 0 && (
                <div>
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Commitments tracked by AI
                  </h4>
                  <div className="space-y-1.5">
                    {ai.commitments.map((c, idx) => {
                      const cs = COMMIT_STATUS_META[(c.status || 'pending').toLowerCase()] || COMMIT_STATUS_META.pending;
                      const CIcon = cs.icon;
                      return (
                        <div key={idx} className="text-xs flex items-start gap-2 bg-white rounded-lg border border-border/40 p-2">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider flex-shrink-0 ${cs.cls}`}>
                            <CIcon className="w-2.5 h-2.5" />
                            {(c.status || 'pending').toUpperCase()}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-foreground"><strong>{c.who}</strong>: {c.what}</p>
                            <div className="flex gap-3 text-[10px] text-muted-foreground mt-0.5">
                              {c.promised_week != null && <span>Promised W{c.promised_week}</span>}
                              {c.actual_week != null && (
                                <span className="flex items-center gap-1"><ArrowRight className="w-2.5 h-2.5" />Actual W{c.actual_week}</span>
                              )}
                            </div>
                            {c.evidence && (
                              <p className="text-[10px] text-muted-foreground/80 italic mt-0.5 line-clamp-1">
                                “{c.evidence}”
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* AI suggested next step */}
              {ai.next_step && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-primary mb-1 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />AI suggested next step
                  </p>
                  <p className="text-xs text-foreground">{ai.next_step}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────
export default function ActionTracker() {
  const navigate = useNavigate();

  const [data, setData]               = useState({ audits: [], counts: {}, total: 0, new_activity_count: 0 });
  const [loading, setLoading]         = useState(true);
  const [stateFilter, setStateFilter] = useState('all');  // 'all' | needs_decision | awaiting | in_progress | resolved
  const [newOnly, setNewOnly]         = useState(false);  // toggle: only audits with new replies
  const [search, setSearch]           = useState('');
  const [expanded, setExpanded]       = useState(new Set());
  const [refreshing, setRefreshing]   = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);

  const fetchAudits = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/tracker/audits`);
      if (r.ok) setData(await r.json());
    } catch (e) {
      toast.error('Could not load audits');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAudits(); }, [fetchAudits]);

  const handleRefreshAll = async () => {
    setRefreshing(true);
    try {
      const r = await fetch(`${BACKEND}/api/tracker/refresh-all`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success(`${d.queued} audit${d.queued !== 1 ? 's' : ''} queued for AI refresh`);
      setTimeout(fetchAudits, 4000);
    } catch (e) {
      toast.error(`Refresh failed: ${e.message}`);
    } finally { setRefreshing(false); }
  };

  const handleSingleRefresh = async (a) => {
    setRefreshingId(a._id);
    try {
      const r = await fetch(`${BACKEND}/api/audits/${a._id}/refresh-thread`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      toast.success(`Refreshed — ${d.message_count} message(s)`);
      fetchAudits();
    } catch (e) {
      toast.error(`Refresh failed: ${e.message}`);
    } finally { setRefreshingId(null); }
  };

  const handleOpen = (a) => navigate(`/results/${a._id}`);

  // Derived: filter + search
  const filtered = data.audits.filter(a => {
    if (newOnly && !a.has_new_activity) return false;
    if (stateFilter !== 'all' && a.decision_state !== stateFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const haystack = [
        a.thread_subject, a.series_display, a.team_member?.name,
        a.team_member?.email, a.country, a.ai?.headline,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const toggleExpand = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-5 h-5 text-primary" />
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              Action Tracker
            </h1>
          </div>
          <p className="text-xs text-muted-foreground">
            Every audit at a glance — see what needs your attention today.
          </p>
        </div>
        <Button
          onClick={handleRefreshAll}
          disabled={refreshing || data.total === 0}
          variant="outline"
          className="gap-2 h-9"
          data-testid="refresh-all-button"
        >
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {refreshing ? 'Refreshing…' : 'Refresh AI on all'}
        </Button>
      </div>

      {/* State tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {['needs_decision', 'awaiting', 'in_progress', 'resolved'].map(s => (
          <StateTile
            key={s}
            stateKey={s}
            count={data.counts?.[s] || 0}
            active={stateFilter === s}
            onClick={() => setStateFilter(stateFilter === s ? 'all' : s)}
          />
        ))}
      </div>

      {/* Filter + search bar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-1 flex-wrap">
          <button
            type="button"
            onClick={() => setStateFilter('all')}
            data-testid="filter-all"
            className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
              stateFilter === 'all'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-white border-border hover:border-primary/30'
            }`}
          >
            All ({data.total || 0})
          </button>
          {/* "New activity" chip — independent toggle alongside the state chips */}
          {(data.new_activity_count || 0) > 0 && (
            <button
              type="button"
              onClick={() => setNewOnly(v => !v)}
              data-testid="filter-new-activity"
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
                newOnly
                  ? 'bg-blue-50 text-blue-700 border-blue-300 ring-1 ring-blue-200'
                  : 'bg-white border-border hover:border-blue-300'
              }`}
              title={newOnly ? 'Showing only audits with new replies — click to clear' : 'Show only audits with new replies since you last opened them'}
            >
              <Sparkles className="w-3 h-3" />
              New activity ({data.new_activity_count})
            </button>
          )}
          {['needs_decision', 'awaiting', 'in_progress', 'resolved'].map(s => {
            const meta = STATE_META[s];
            const active = stateFilter === s;
            return (
              <button
                key={s} type="button"
                onClick={() => setStateFilter(active ? 'all' : s)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
                  active ? meta.pill + ' border-current' : 'bg-white border-border hover:border-primary/30'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                {meta.short}
              </button>
            );
          })}
        </div>
        <div className="flex-1 min-w-[180px]">
          <input
            type="text"
            placeholder="Filter by subject, team member, country…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="audit-search-input"
            className="w-full h-8 px-3 text-xs border border-border rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-primary/40"
          />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-2.5">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}
        </div>
      ) : data.total === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <Target className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm font-medium text-muted-foreground">No audits yet</p>
            <p className="text-xs text-muted-foreground/80 mt-1 mb-4">
              Audit a Gmail thread to see it tracked here.
            </p>
            <Button onClick={() => navigate('/emails')} className="gap-2">
              <Mail className="w-4 h-4" />Browse Gmail
            </Button>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No audits match your filter. <button type="button" onClick={() => { setStateFilter('all'); setSearch(''); }} className="text-primary underline">Clear filters</button>
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2.5" data-testid="audit-list">
          <AnimatePresence mode="popLayout">
            {filtered.map(a => (
              <AuditCard
                key={a._id}
                a={a}
                expanded={expanded.has(a._id)}
                onToggle={() => toggleExpand(a._id)}
                onOpen={handleOpen}
                onRefresh={handleSingleRefresh}
                refreshing={refreshingId === a._id}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
